import { useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useAppContext } from '../../context/AppContext';
import { useCards, useCustomer, useRemoveCard, useUpdateCustomerBillingDetails } from '../../hooks/payment';
import styles from './BillingDetails.module.scss';
import {
  UserOutlined,
  MailOutlined,
  BankOutlined,
  EditOutlined,
  EnvironmentOutlined,
} from '@ant-design/icons';
import {
  Skeleton,
  Alert,
  Button,
  Modal,
  Form,
  Input,
  Row,
  Col,
  Select,
} from 'antd';
import {
  postcodeValidator,
  postcodeValidatorExistsForCountry,
} from 'postcode-validator';
import visaIcon from '../../assets/images/visa.png';
import mastercardIcon from '../../assets/images/mastercard.png';
import Warning from '../../components/Warning';

const COUNTRY_CODES = [
  {
    text: 'Afghanistan',
    value: 'AF',
  },
  {
    text: 'Aland Islands',
    value: 'AX',
  },
  {
    text: 'Albania',
    value: 'AL',
  },
  {
    text: 'Algeria',
    value: 'DZ',
  },
  {
    text: 'American Samoa',
    value: 'AS',
  },
  {
    text: 'Andorra',
    value: 'AD',
  },
  {
    text: 'Angola',
    value: 'AO',
  },
  {
    text: 'Anguilla',
    value: 'AI',
  },
  {
    text: 'Antarctica',
    value: 'AQ',
  },
  {
    text: 'Antigua And Barbuda',
    value: 'AG',
  },
  {
    text: 'Argentina',
    value: 'AR',
  },
  {
    text: 'Armenia',
    value: 'AM',
  },
  {
    text: 'Aruba',
    value: 'AW',
  },
  {
    text: 'Australia',
    value: 'AU',
  },
  {
    text: 'Austria',
    value: 'AT',
  },
  {
    text: 'Azerbaijan',
    value: 'AZ',
  },
  {
    text: 'Bahamas',
    value: 'BS',
  },
  {
    text: 'Bahrain',
    value: 'BH',
  },
  {
    text: 'Bangladesh',
    value: 'BD',
  },
  {
    text: 'Barbados',
    value: 'BB',
  },
  {
    text: 'Belarus',
    value: 'BY',
  },
  {
    text: 'Belgium',
    value: 'BE',
  },
  {
    text: 'Belize',
    value: 'BZ',
  },
  {
    text: 'Benin',
    value: 'BJ',
  },
  {
    text: 'Bermuda',
    value: 'BM',
  },
  {
    text: 'Bhutan',
    value: 'BT',
  },
  {
    text: 'Bolivia',
    value: 'BO',
  },
  {
    text: 'Bosnia And Herzegovina',
    value: 'BA',
  },
  {
    text: 'Botswana',
    value: 'BW',
  },
  {
    text: 'Bouvet Island',
    value: 'BV',
  },
  {
    text: 'Brazil',
    value: 'BR',
  },
  {
    text: 'British Indian Ocean Territory',
    value: 'IO',
  },
  {
    text: 'British Virgin Islands',
    value: 'VG',
  },
  {
    text: 'Brunei Darussalam',
    value: 'BN',
  },
  {
    text: 'Bulgaria',
    value: 'BG',
  },
  {
    text: 'Burkina Faso',
    value: 'BF',
  },
  {
    text: 'Burundi',
    value: 'BI',
  },
  {
    text: 'Cambodia',
    value: 'KH',
  },
  {
    text: 'Cameroon',
    value: 'CM',
  },
  {
    text: 'Canada',
    value: 'CA',
  },
  {
    text: 'Canary Islands',
    value: 'IC',
  },
  {
    text: 'Cape Verde',
    value: 'CV',
  },
  {
    text: 'Cayman Islands',
    value: 'KY',
  },
  {
    text: 'Central African Republic',
    value: 'CF',
  },
  {
    text: 'Ceuta And Melilla',
    value: 'EA',
  },
  {
    text: 'Chad',
    value: 'TD',
  },
  {
    text: 'Chile',
    value: 'CL',
  },
  {
    text: 'China',
    value: 'CN',
  },
  {
    text: 'Christmas Island',
    value: 'CX',
  },
  {
    text: 'Cocos (Keeling) Islands',
    value: 'CC',
  },
  {
    text: 'Colombia',
    value: 'CO',
  },
  {
    text: 'Comoros',
    value: 'KM',
  },
  {
    text: 'Congo',
    value: 'CG',
  },
  {
    text: 'Congo (Democratic Republic)',
    value: 'CD',
  },
  {
    text: 'Cook Islands',
    value: 'CK',
  },
  {
    text: 'Costa Rica',
    value: 'CR',
  },
  {
    text: 'Croatia',
    value: 'HR',
  },
  {
    text: 'Curaçao',
    value: 'CW',
  },
  {
    text: 'Cyprus',
    value: 'CY',
  },
  {
    text: 'Czech Republic',
    value: 'CZ',
  },
  {
    text: "Côte D'ivoire",
    value: 'CI',
  },
  {
    text: 'Denmark',
    value: 'DK',
  },
  {
    text: 'Djibouti',
    value: 'DJ',
  },
  {
    text: 'Dominica',
    value: 'DM',
  },
  {
    text: 'Dominican Republic',
    value: 'DO',
  },
  {
    text: 'Ecuador',
    value: 'EC',
  },
  {
    text: 'Egypt',
    value: 'EG',
  },
  {
    text: 'El Salvador',
    value: 'SV',
  },
  {
    text: 'Equatorial Guinea',
    value: 'GQ',
  },
  {
    text: 'Eritrea',
    value: 'ER',
  },
  {
    text: 'Estonia',
    value: 'EE',
  },
  {
    text: 'Ethiopia',
    value: 'ET',
  },
  {
    text: 'Falkland Islands (Malvinas)',
    value: 'FK',
  },
  {
    text: 'Faroe Islands',
    value: 'FO',
  },
  {
    text: 'Fiji',
    value: 'FJ',
  },
  {
    text: 'Finland',
    value: 'FI',
  },
  {
    text: 'France',
    value: 'FR',
  },
  {
    text: 'French Guiana',
    value: 'GF',
  },
  {
    text: 'French Polynesia',
    value: 'PF',
  },
  {
    text: 'French Southern Territories',
    value: 'TF',
  },
  {
    text: 'Gabon',
    value: 'GA',
  },
  {
    text: 'Gambia',
    value: 'GM',
  },
  {
    text: 'Georgia',
    value: 'GE',
  },
  {
    text: 'Germany',
    value: 'DE',
  },
  {
    text: 'Ghana',
    value: 'GH',
  },
  {
    text: 'Gibraltar',
    value: 'GI',
  },
  {
    text: 'Greece',
    value: 'GR',
  },
  {
    text: 'Greenland',
    value: 'GL',
  },
  {
    text: 'Grenada',
    value: 'GD',
  },
  {
    text: 'Guadeloupe',
    value: 'GP',
  },
  {
    text: 'Guam',
    value: 'GU',
  },
  {
    text: 'Guatemala',
    value: 'GT',
  },
  {
    text: 'Guernsey',
    value: 'GG',
  },
  {
    text: 'Guinea',
    value: 'GN',
  },
  {
    text: 'Guinea-bissau',
    value: 'GW',
  },
  {
    text: 'Guyana',
    value: 'GY',
  },
  {
    text: 'Haiti',
    value: 'HT',
  },
  {
    text: 'Heard Island And Mcdonald Islands',
    value: 'HM',
  },
  {
    text: 'Honduras',
    value: 'HN',
  },
  {
    text: 'Hong Kong',
    value: 'HK',
  },
  {
    text: 'Hungary',
    value: 'HU',
  },
  {
    text: 'Iceland',
    value: 'IS',
  },
  {
    text: 'India',
    value: 'IN',
  },
  {
    text: 'Indonesia',
    value: 'ID',
  },
  {
    text: 'Iraq',
    value: 'IQ',
  },
  {
    text: 'Ireland',
    value: 'IE',
  },
  {
    text: 'Isle Of Man',
    value: 'IM',
  },
  {
    text: 'Israel',
    value: 'IL',
  },
  {
    text: 'Italy',
    value: 'IT',
  },
  {
    text: 'Jamaica',
    value: 'JM',
  },
  {
    text: 'Japan',
    value: 'JP',
  },
  {
    text: 'Jersey',
    value: 'JE',
  },
  {
    text: 'Jordan',
    value: 'JO',
  },
  {
    text: 'Kazakhstan',
    value: 'KZ',
  },
  {
    text: 'Kenya',
    value: 'KE',
  },
  {
    text: 'Kiribati',
    value: 'KI',
  },
  {
    text: 'Korea (Republic)',
    value: 'KR',
  },
  {
    text: 'Kosovo',
    value: 'XK',
  },
  {
    text: 'Kuwait',
    value: 'KW',
  },
  {
    text: 'Kyrgyzstan',
    value: 'KG',
  },
  {
    text: "Lao (People's Democratic Republic)",
    value: 'LA',
  },
  {
    text: 'Latvia',
    value: 'LV',
  },
  {
    text: 'Lebanon',
    value: 'LB',
  },
  {
    text: 'Lesotho',
    value: 'LS',
  },
  {
    text: 'Liberia',
    value: 'LR',
  },
  {
    text: 'Libya',
    value: 'LY',
  },
  {
    text: 'Liechtenstein',
    value: 'LI',
  },
  {
    text: 'Lithuania',
    value: 'LT',
  },
  {
    text: 'Luxembourg',
    value: 'LU',
  },
  {
    text: 'Macao',
    value: 'MO',
  },
  {
    text: 'Macedonia',
    value: 'MK',
  },
  {
    text: 'Madagascar',
    value: 'MG',
  },
  {
    text: 'Malawi',
    value: 'MW',
  },
  {
    text: 'Malaysia',
    value: 'MY',
  },
  {
    text: 'Maldives',
    value: 'MV',
  },
  {
    text: 'Mali',
    value: 'ML',
  },
  {
    text: 'Malta',
    value: 'MT',
  },
  {
    text: 'Marshall Islands',
    value: 'MH',
  },
  {
    text: 'Martinique',
    value: 'MQ',
  },
  {
    text: 'Mauritania',
    value: 'MR',
  },
  {
    text: 'Mauritius',
    value: 'MU',
  },
  {
    text: 'Mayotte',
    value: 'YT',
  },
  {
    text: 'Mexico',
    value: 'MX',
  },
  {
    text: 'Micronesia',
    value: 'FM',
  },
  {
    text: 'Moldova',
    value: 'MD',
  },
  {
    text: 'Monaco',
    value: 'MC',
  },
  {
    text: 'Mongolia',
    value: 'MN',
  },
  {
    text: 'Montenegro',
    value: 'ME',
  },
  {
    text: 'Montserrat',
    value: 'MS',
  },
  {
    text: 'Morocco',
    value: 'MA',
  },
  {
    text: 'Mozambique',
    value: 'MZ',
  },
  {
    text: 'Myanmar',
    value: 'MM',
  },
  {
    text: 'Namibia',
    value: 'NA',
  },
  {
    text: 'Nauru',
    value: 'NR',
  },
  {
    text: 'Nepal',
    value: 'NP',
  },
  {
    text: 'Netherlands',
    value: 'NL',
  },
  {
    text: 'Netherlands Antilles',
    value: 'AN',
  },
  {
    text: 'New Caledonia',
    value: 'NC',
  },
  {
    text: 'New Zealand',
    value: 'NZ',
  },
  {
    text: 'Nicaragua',
    value: 'NI',
  },
  {
    text: 'Niger',
    value: 'NE',
  },
  {
    text: 'Nigeria',
    value: 'NG',
  },
  {
    text: 'Niue',
    value: 'NU',
  },
  {
    text: 'Norfolk Island',
    value: 'NF',
  },
  {
    text: 'Northern Mariana Islands',
    value: 'MP',
  },
  {
    text: 'Norway',
    value: 'NO',
  },
  {
    text: 'Oman',
    value: 'OM',
  },
  {
    text: 'Pakistan',
    value: 'PK',
  },
  {
    text: 'Palau',
    value: 'PW',
  },
  {
    text: 'Palestinian Territory (Occupied)',
    value: 'PS',
  },
  {
    text: 'Panama',
    value: 'PA',
  },
  {
    text: 'Papua New Guinea',
    value: 'PG',
  },
  {
    text: 'Paraguay',
    value: 'PY',
  },
  {
    text: 'Peru',
    value: 'PE',
  },
  {
    text: 'Philippines',
    value: 'PH',
  },
  {
    text: 'Pitcairn',
    value: 'PN',
  },
  {
    text: 'Poland',
    value: 'PL',
  },
  {
    text: 'Portugal',
    value: 'PT',
  },
  {
    text: 'Puerto Rico',
    value: 'PR',
  },
  {
    text: 'Qatar',
    value: 'QA',
  },
  {
    text: 'Reunion',
    value: 'RE',
  },
  {
    text: 'Romania',
    value: 'RO',
  },
  {
    text: 'Russian Federation',
    value: 'RU',
  },
  {
    text: 'Rwanda',
    value: 'RW',
  },
  {
    text: 'Saint Barthélemy',
    value: 'BL',
  },
  {
    text: 'Saint Helena',
    value: 'SH',
  },
  {
    text: 'Saint Kitts And Nevis',
    value: 'KN',
  },
  {
    text: 'Saint Lucia',
    value: 'LC',
  },
  {
    text: 'Saint Martin (French Part)',
    value: 'MF',
  },
  {
    text: 'Saint Pierre And Miquelon',
    value: 'PM',
  },
  {
    text: 'Saint Vincent And The Grenadines',
    value: 'VC',
  },
  {
    text: 'Samoa',
    value: 'WS',
  },
  {
    text: 'San Marino',
    value: 'SM',
  },
  {
    text: 'Sao Tome And Principe',
    value: 'ST',
  },
  {
    text: 'Saudi Arabia',
    value: 'SA',
  },
  {
    text: 'Senegal',
    value: 'SN',
  },
  {
    text: 'Serbia',
    value: 'RS',
  },
  {
    text: 'Seychelles',
    value: 'SC',
  },
  {
    text: 'Sierra Leone',
    value: 'SL',
  },
  {
    text: 'Singapore',
    value: 'SG',
  },
  {
    text: 'Sint Eustatius And Saba Bonaire',
    value: 'BQ',
  },
  {
    text: 'Sint Maarten (Dutch Part)',
    value: 'SX',
  },
  {
    text: 'Slovakia',
    value: 'SK',
  },
  {
    text: 'Slovenia',
    value: 'SI',
  },
  {
    text: 'Solomon Islands',
    value: 'SB',
  },
  {
    text: 'Somalia',
    value: 'SO',
  },
  {
    text: 'South Africa',
    value: 'ZA',
  },
  {
    text: 'South Georgia And The South Sandwich Islands',
    value: 'GS',
  },
  {
    text: 'South Sudan',
    value: 'SS',
  },
  {
    text: 'Spain',
    value: 'ES',
  },
  {
    text: 'Sri Lanka',
    value: 'LK',
  },
  {
    text: 'Sudan',
    value: 'SD',
  },
  {
    text: 'Suriname',
    value: 'SR',
  },
  {
    text: 'Svalbard And Jan Mayen',
    value: 'SJ',
  },
  {
    text: 'Swaziland',
    value: 'SZ',
  },
  {
    text: 'Sweden',
    value: 'SE',
  },
  {
    text: 'Switzerland',
    value: 'CH',
  },
  {
    text: 'Taiwan',
    value: 'TW',
  },
  {
    text: 'Tajikistan',
    value: 'TJ',
  },
  {
    text: 'Tanzania',
    value: 'TZ',
  },
  {
    text: 'Thailand',
    value: 'TH',
  },
  {
    text: 'Timor-leste',
    value: 'TL',
  },
  {
    text: 'Togo',
    value: 'TG',
  },
  {
    text: 'Tokelau',
    value: 'TK',
  },
  {
    text: 'Tonga',
    value: 'TO',
  },
  {
    text: 'Trinidad And Tobago',
    value: 'TT',
  },
  {
    text: 'Tunisia',
    value: 'TN',
  },
  {
    text: 'Turkey',
    value: 'TR',
  },
  {
    text: 'Turkmenistan',
    value: 'TM',
  },
  {
    text: 'Turks And Caicos Islands',
    value: 'TC',
  },
  {
    text: 'Tuvalu',
    value: 'TV',
  },
  {
    text: 'U.S. Virgin Islands',
    value: 'VI',
  },
  {
    text: 'Uganda',
    value: 'UG',
  },
  {
    text: 'Ukraine',
    value: 'UA',
  },
  {
    text: 'United Arab Emirates',
    value: 'AE',
  },
  {
    text: 'United Kingdom',
    value: 'GB',
  },
  {
    text: 'United Kingdom (Northern Ireland)',
    value: 'XI',
  },
  {
    text: 'United States',
    value: 'US',
  },
  {
    text: 'United States Minor Outlying Islands',
    value: 'UM',
  },
  {
    text: 'Uruguay',
    value: 'UY',
  },
  {
    text: 'Uzbekistan',
    value: 'UZ',
  },
  {
    text: 'Vanuatu',
    value: 'VU',
  },
  {
    text: 'Vatican City',
    value: 'VA',
  },
  {
    text: 'Venezuela',
    value: 'VE',
  },
  {
    text: 'Viet Nam',
    value: 'VN',
  },
  {
    text: 'Wallis And Futuna',
    value: 'WF',
  },
  {
    text: 'Western Sahara',
    value: 'EH',
  },
  {
    text: 'Yemen',
    value: 'YE',
  },
  {
    text: 'Zambia',
    value: 'ZM',
  },
  {
    text: 'Zimbabwe',
    value: 'ZW',
  },
];

const BillingDetails = () => {
  const { pCode } = useAppContext();
  const queryClient = useQueryClient();
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isRemoveModalOpen, setIsRemoveModalOpen] = useState(false);
  const [cardToRemove, setCardToRemove] = useState<string | null>(null);
  const [updateError, setUpdateError] = useState<string | null>(null);
  const [showSuccessMessage, setShowSuccessMessage] = useState(false);
  const [form] = Form.useForm();

  const {
    data: customer,
    isLoading: isCustomerLoading,
    isError: isCustomerError,
  } = useCustomer(pCode);
  const {
    data: cards,
    isLoading: isCardsLoading,
    isError: isCardsError,
  } = useCards(pCode);

  const { mutate: removeCard, isPending: isRemovingCard } = useRemoveCard();
  const { mutate: updateCustomerBillingDetails, isPending: isUpdatingCustomerBillingDetails } = useUpdateCustomerBillingDetails();

  const handleUpdateCustomerBillingDetails = (data: any) => {
    setUpdateError(null); // Clear any previous errors
    updateCustomerBillingDetails(
      { pcode: pCode, data: data },
      {
        onSuccess: () => {
          queryClient.invalidateQueries({ queryKey: ['customer', pCode] });
          setIsEditModalOpen(false);
          setUpdateError(null);
          setShowSuccessMessage(true);
        },
        onError: (error: any) => {
          console.error('Failed to update customer billing details:', error);
          const errorMessage = error?.response?.data?.message || error?.message || 'Failed to update billing details. Please try again.';
          setUpdateError(errorMessage);
        },
      }
    );
  };

  const handleRemoveCard = (cardId: string) => {
    setCardToRemove(cardId);
    setIsRemoveModalOpen(true);
  };

  const handleConfirmRemove = () => {
    if (cardToRemove) {
      removeCard(
        { pcode: pCode, cardId: cardToRemove },
        {
          onSuccess: () => {
            // Invalidate and refetch cards
            queryClient.invalidateQueries({ queryKey: ['cards', pCode] });
            setIsRemoveModalOpen(false);
            setCardToRemove(null);
          },
          onError: (error) => {
            console.error('Failed to remove card:', error);
            // You can add error notification here
            setIsRemoveModalOpen(false);
            setCardToRemove(null);
          },
        }
      );
    }
  };

  const handleCancelRemove = () => {
    setIsRemoveModalOpen(false);
    setCardToRemove(null);
  };

  const handleEditDetails = () => {
    form.setFieldsValue({
      firstName: customer?.billingInfo?.firstName || '',
      lastName: customer?.billingInfo?.lastName || '',
      email: customer?.email || '',
      company: customer?.billingInfo?.company || '',
      line1: customer?.billingInfo?.line1 || '',
      line2: customer?.billingInfo?.line2 || '',
      city: customer?.billingInfo?.city || '',
      state: customer?.billingInfo?.state || '',
      zip: customer?.billingInfo?.zip || '',
      country: customer?.billingInfo?.country || '',
    });
    setUpdateError(null); // Clear any previous errors
    setIsEditModalOpen(true);
  };

  const handleEditModalOk = () => {
    form.validateFields().then((values) => {
      console.log('Updated values:', values);
      // Add your update logic here
      handleUpdateCustomerBillingDetails(values);
    });
  };

  const handleEditModalCancel = () => {
    setIsEditModalOpen(false);
    setUpdateError(null); // Clear error when closing modal
  };

  return (
    <div>
      {/* Top Section - Basic Details and Chargebee Info Side by Side */}
      <div className={styles.topSection}>
        <div className={styles.billingCard}>
          <div className={styles.billingCardHeader}>
            <p className={styles.billingCardTitle}>Basic Details</p>
            <Button
              type="link"
              icon={<EditOutlined />}
              onClick={handleEditDetails}
              disabled={isCustomerLoading || isCustomerError}
            >
              Edit Details
            </Button>
          </div>
          {isCustomerLoading ? (
            <div className={styles.skeletonWrapper}>
              <Skeleton active paragraph={{ rows: 4 }} />
            </div>
          ) : isCustomerError ? (
            <Alert
              message="Failed to load customer details"
              description="Unable to fetch customer information. Please try again later."
              type="error"
              showIcon
            />
          ) : (
            <>
              <div className={styles.row}>
                <div className={styles.column}>
                  <div className={styles.label}>
                    <UserOutlined /> First name
                  </div>
                  <div className={styles.value}>
                    {customer?.billingInfo?.firstName ?? '-'}
                  </div>
                </div>
                <div className={styles.column}>
                  <div className={styles.label}>
                    <UserOutlined /> Last Name
                  </div>
                  <div className={styles.value}>
                    {customer?.billingInfo?.lastName ?? '-'}
                  </div>
                </div>
              </div>

              {/* Second Row - Email and Organization */}
              <div className={styles.row}>
                <div className={styles.column}>
                  <div className={styles.label}>
                    <MailOutlined /> Email Id
                  </div>
                  <div className={styles.value}>{customer?.billingInfo?.email ?? '-'}</div>
                </div>
                <div className={styles.column}>
                  <div className={styles.label}>
                    <BankOutlined /> Company Name
                  </div>
                  <div className={styles.value}>
                    {customer?.billingInfo?.company ?? '-'}
                  </div>
                </div>
              </div>

              {/* Third Row - Address Lines */}
              <div className={styles.row}>
                <div className={styles.column}>
                  <div className={styles.label}>
                    <EnvironmentOutlined /> Address Line 1
                  </div>
                  <div className={styles.value}>{customer?.billingInfo?.line1 ?? '-'}</div>
                </div>
                <div className={styles.column}>
                  <div className={styles.label}>
                    <EnvironmentOutlined /> Address Line 2
                  </div>
                  <div className={styles.value}>{customer?.billingInfo?.line2 ?? '-'}</div>
                </div>
              </div>

              {/* Fourth Row - City and State */}
              <div className={styles.row}>
                <div className={styles.column}>
                  <div className={styles.label}>
                    <EnvironmentOutlined /> City
                  </div>
                  <div className={styles.value}>{customer?.billingInfo?.city ?? '-'}</div>
                </div>
                <div className={styles.column}>
                  <div className={styles.label}>
                    <EnvironmentOutlined /> State
                  </div>
                  <div className={styles.value}>{customer?.billingInfo?.state ?? '-'}</div>
                </div>
              </div>

              {/* Fifth Row - Zip and Country */}
              <div className={styles.row}>
                <div className={styles.column}>
                  <div className={styles.label}>
                    <EnvironmentOutlined /> Zip Code
                  </div>
                  <div className={styles.value}>{customer?.billingInfo?.zip ?? '-'}</div>
                </div>
                <div className={styles.column}>
                  <div className={styles.label}>
                    <EnvironmentOutlined /> Country
                  </div>
                  <div className={styles.value}>
                    {customer?.billingInfo?.country
                      ? COUNTRY_CODES.find((c) => c.value === customer?.billingInfo?.country)?.text ?? customer?.billingInfo?.country
                      : '-'}
                  </div>
                </div>
              </div>
            </>
          )}
        </div>
      </div>

      {/* Payment Cards Card */}
      <div className={styles.paymentCard}>
        <p className={styles.paymentCardTitle}>Payment Cards</p>

        {isCardsLoading && (
          <div className={styles.skeletonWrapper}>
            <Skeleton active avatar paragraph={{ rows: 1 }} />
          </div>
        )}

        {isCardsError && (
          <Alert
            message="Failed to load payment cards"
            description="Unable to fetch payment information. Please try again later."
            type="error"
            showIcon
          />
        )}

        {!isCardsLoading && !isCardsError && cards && cards.length > 0 && (
          <>
            {cards.map((card) => (
              <div className={styles.paymentMethod} key={card.cardId}>
                <div className={styles.cardInfo}>
                  <div className={styles.cardIcon}>
                    <img
                      src={card.cardType === 'visa' ? visaIcon : mastercardIcon}
                      alt={card.cardType}
                      className={styles.cardIconImage}
                    />
                  </div>
                  <div className={styles.cardDetails}>
                    <div className={styles.cardNumber}>
                      {card.maskedCardNumber}
                    </div>
                    <div className={styles.cardMeta}>
                      {card.cardType} | Expiry: {card.expiryDate}
                    </div>
                  </div>
                </div>
                <Button
                  type="link"
                  danger
                  onClick={() => handleRemoveCard(card.cardId)}
                  className={styles.removeCardButton}
                >
                  Remove Card
                </Button>
              </div>
            ))}
          <Warning title='' asList={false} warnings={['Removing a card with any active subscriptions linked to the card will fail to renew due to payment failure.']} />
          </>
        )}

        {!isCardsLoading && !isCardsError && (!cards || cards.length === 0) && (
          <p className={styles.paymentCardDescription}>
            No payment cards found
          </p>
        )}
      </div>

      <Modal
        title="Edit Billing Details"
        open={isEditModalOpen}
        onOk={handleEditModalOk}
        onCancel={handleEditModalCancel}
        okText="Update"
        cancelText="Cancel"
        width={600}
        okButtonProps={{ loading: isUpdatingCustomerBillingDetails }}
        cancelButtonProps={{ disabled: isUpdatingCustomerBillingDetails }}
        closable={!isUpdatingCustomerBillingDetails}
        maskClosable={!isUpdatingCustomerBillingDetails}
      >
        {updateError && (
          <Alert
            message="Update Failed"
            description={updateError}
            type="error"
            showIcon
            closable
            onClose={() => setUpdateError(null)}
            style={{ marginBottom: 16 }}
          />
        )}
        <Form form={form} layout="vertical">
          {/* Row 1: First Name & Last Name */}
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="firstName"
                label="First Name"
                rules={[{ required: true, message: 'Please enter first name' }]}
              >
                <Input prefix={<UserOutlined />} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="lastName"
                label="Last Name"
                rules={[{ required: true, message: 'Please enter last name' }]}
              >
                <Input prefix={<UserOutlined />} />
              </Form.Item>
            </Col>
          </Row>

          {/* Row 2: Email & Company Name */}
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="email"
                label="Email Id"
                rules={[
                  { required: true, message: 'Please enter email' },
                  { type: 'email', message: 'Please enter a valid email' },
                ]}
              >
                <Input prefix={<MailOutlined />} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="company" label="Company Name">
                <Input prefix={<BankOutlined />} />
              </Form.Item>
            </Col>
          </Row>

          {/* Billing Address Section */}
          <div className={styles.formSectionTitle}>Billing Address</div>

          {/* Row 3: Address Line 1 & Address Line 2 */}
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="line1"
                label="Address Line 1"
                rules={[{ required: true, message: 'Please enter address' }]}
              >
                <Input prefix={<EnvironmentOutlined />} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="line2" label="Address Line 2">
                <Input prefix={<EnvironmentOutlined />} />
              </Form.Item>
            </Col>
          </Row>

          {/* Row 4: Zip & Country */}
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="zip"
                label="Zip"
                dependencies={['country']}
                rules={[
                  { required: true, message: 'Please enter zip code' },
                  ({ getFieldValue }) => ({
                    validator(_, value) {
                      if (!value) {
                        return Promise.resolve();
                      }
                      const country = getFieldValue('country');
                      if (!country) {
                        return Promise.resolve();
                      }
                      if (!postcodeValidatorExistsForCountry(country)) {
                        return Promise.resolve();
                      }
                      if (postcodeValidator(value, country)) {
                        return Promise.resolve();
                      }
                      return Promise.reject(new Error('Invalid ZipCode'));
                    },
                  }),
                ]}
              >
                <Input />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="country"
                label="Country"
                rules={[{ required: true, message: 'Please select country' }]}
              >
                <Select placeholder="Select country" showSearch
                optionFilterProp="children"
                filterOption={(input, option) =>
                  typeof option?.children === 'string' &&
                  (option.children as string).toLowerCase().includes(input.toLowerCase())
                }
                >
                  {COUNTRY_CODES.map((country) => (
                    <Select.Option key={country.value} value={country.value}>
                      {country.text}
                    </Select.Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
          </Row>

          {/* Row 5: State & City */}
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="state"
                label="State"
              >
                <Input />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="city"
                label="City"
              >
                <Input />
              </Form.Item>
            </Col>
          </Row>
        </Form>
      </Modal>

      {/* Remove Card Confirmation Modal */}
      <Modal
        title="Are you sure you want to remove this card?"
        open={isRemoveModalOpen}
        onOk={handleConfirmRemove}
        onCancel={handleCancelRemove}
        okText="Yes, Remove"
        cancelText="Cancel"
        okButtonProps={{ danger: true, type: 'primary', loading: isRemovingCard }}
        cancelButtonProps={{ disabled: isRemovingCard }}
        closable={!isRemovingCard}
        maskClosable={!isRemovingCard}
      >
        <p>This action cannot be undone. Any subscriptions linked to this card will fail to renew due to payment failure.</p>
        <br />

        <small style={{ color: 'var(--gray-8)', fontSize: '12px', marginTop: '16px' }}><strong>Note: </strong>You can create a new subscription for your property using a different payment method.</small>
      </Modal>

      {showSuccessMessage && (
        <Modal
          title=""
          open={showSuccessMessage}
          onOk={() => setShowSuccessMessage(false)}
        >
          <p>Your billing details have been updated and will be applied to all future invoices.</p>
        </Modal>
      )}
    </div>
  );
};

export default BillingDetails;
